// MeetTheTeam.js
import React from "react";
import { Heart, Shield, Users, Award, Phone } from "lucide-react";

const MeetTheTeam = () => {
  const values = [
    {
      icon: Heart,
      title: "Compassion First",
      description:
        "Every member of our team is driven by genuine care and empathy for the seniors and families we serve.",
    },
    {
      icon: Shield,
      title: "Trust & Safety",
      description:
        "All caregivers undergo rigorous background checks, training, and certification to ensure the highest standards of care.",
    },
    {
      icon: Users,
      title: "Family-Centered Care",
      description:
        "We work closely with families, treating each client as we would our own loved ones, with dignity and respect.",
    },
    {
      icon: Award,
      title: "Excellence in Service",
      description:
        "Our commitment to ongoing training and professional development ensures exceptional care delivery every day.",
    },
  ];

  const teamHighlights = [
    {
      title: "Certified & Trained Professionals",
      description:
        "Our caregivers hold relevant certifications and receive continuous training in aged care, dementia support, mobility assistance, and emergency response.",
    },
    {
      title: "Experienced Care Coordinators",
      description:
        "Our care coordination team has decades of combined experience in healthcare and aged care, ensuring seamless service delivery and personalised care plans.",
    },
    {
      title: "Compassionate Support Staff",
      description:
        "From your first call to ongoing care, our administrative team provides friendly, responsive support to address your questions and concerns.",
    },
    {
      title: "Specialised Care Experts",
      description:
        "We have caregivers with specialised training in areas such as Alzheimer's care, spinal cord injury support, hospice care, and post-operative recovery.",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gray-50 py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Meet Our Caring Team
          </h1>
          <p className="text-lg text-gray-700 font-light mb-8">
            At Allawah Lodge, our team is our greatest strength. We're a
            dedicated group of professionals united by a shared mission: to
            provide exceptional, compassionate care that helps seniors live with
            dignity, comfort, and independence in their own homes.
          </p>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Our Core Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#E85D2C] rounded-full mb-4">
                    <Icon size={32} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-700 font-light leading-relaxed">
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Highlights Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Who We Are
          </h2>
          <div className="space-y-8">
            {teamHighlights.map((highlight, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {highlight.title}
                </h3>
                <p className="text-gray-700 font-light leading-relaxed">
                  {highlight.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Our Commitment to You
          </h2>
          <div className="space-y-4 text-gray-700 font-light leading-relaxed text-center mb-8">
            <p>
              Every member of the Allawah team—from caregivers to
              coordinators—is carefully selected for their skills, experience,
              and most importantly, their heart for helping others. We believe
              that quality care goes beyond medical support; it's about building
              meaningful relationships, offering emotional support, and treating
              every client with the respect and dignity they deserve.
            </p>
            <p>
              When you choose Allawah Lodge, you're not just hiring a
              service—you're welcoming a team of dedicated professionals who
              genuinely care about your loved one's well-being and your family's
              peace of mind.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#AD2647] to-[#E85D2C] py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Meet Your Care Team?
          </h2>
          <p className="text-lg text-white font-light mb-8">
            Contact us today to schedule your complimentary in-home assessment
            and meet the caregivers who will support your family.
          </p>
          <a
            href="tel:+61269272706"
            className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 text-[#E85D2C] font-medium px-6 py-3 rounded-full transition-colors"
          >
            <Phone size={20} />
            CALL: (02) 6927 2706
          </a>
        </div>
      </section>
    </div>
  );
};

export default MeetTheTeam;
